import { runPathId } from './runPath';
import { apiClient, idempotentConfig } from './client';
import type {
  ApiEnvelope,
  RunIndexItem,
  RunIndexPage,
  RunStartResult,
  RunStateView,
  StartRunRequest,
  DecisionChainView,
} from './types';

/**
 * Runs — the run-index browser (GET /v1/runs over the PS-3 product_run_index projection), the
 * live flow-runtime state read (GET /v1/runs/{id}), and the RATIFIED start door
 * (POST /v1/runs, `runs.start`). List never touches Temporal; the detail and the start do.
 */

export interface RunsQuery {
  flowId?: string;
  entityId?: string;
  runId?: string;
  status?: 'ACTIVE' | 'COMPLETED';
  page?: number;
  pageSize?: number;
}

export async function listRuns(q: RunsQuery = {}): Promise<RunIndexPage> {
  const { data } = await apiClient.get<ApiEnvelope<RunIndexPage>>('/v1/runs', {
    params: {
      flowId: q.flowId,
      entityId: q.entityId,
      runId: q.runId,
      status: q.status,
      page: q.page ?? 1,
      pageSize: q.pageSize ?? 50,
    },
  });
  return data.data;
}

export async function getRunState(runId: string): Promise<RunStateView> {
  const { data } = await apiClient.get<ApiEnvelope<RunStateView>>(
    `/v1/runs/${runPathId(runId)}`,
  );
  return data.data;
}

export async function getRunDecisions(runId: string): Promise<DecisionChainView> {
  const { data } = await apiClient.get<ApiEnvelope<DecisionChainView>>(
    `/v1/runs/${runPathId(runId)}/decisions`,
  );
  return data.data;
}

/** The run's durable index row. It names the run's subject, whatever a link assumed about it. */
export async function findRunIndexItem(runId: string): Promise<RunIndexItem | null> {
  const res = await listRuns({ runId, pageSize: 1 });
  return res.items.find((r) => r.runId === runId) ?? null;
}

/**
 * POST /v1/runs — start a PUBLISHED flow (OPSFL-FLOWOPS-1 A1: the console's "Start run"). The body
 * is the ratified `StartRunRequest` verbatim; the `ol-idempotency-key` ULID makes the console's
 * own retry safe, while `runId` is the run's identity (Temporal rejects a duplicate `(hub, runId)`
 * start — the natural idempotency key named in the contract). Server answers 202 with the
 * `RunStartResult`; refusals: 403 (`runs.start`), 400 (unpublished/version missing), 404 (no such
 * flow/version), 409 (idempotency key reused with a different body), 503 (runtime disabled).
 * Pass `idemKey` to retry the SAME logical start without a duplicate side effect.
 */
export async function startRun(req: StartRunRequest, idemKey?: string): Promise<RunStartResult> {
  const body: StartRunRequest = { flowId: req.flowId, runId: req.runId };
  if (req.version != null) body.version = req.version;
  if (req.entityId) body.entityId = req.entityId;
  if (req.input && Object.keys(req.input).length > 0) body.input = req.input;
  const { data } = await apiClient.post<ApiEnvelope<RunStartResult>>(
    '/v1/runs',
    body,
    idempotentConfig(idemKey),
  );
  return data.data;
}

export type { RunIndexItem, RunIndexPage, RunStartResult, RunStateView, StartRunRequest };
